import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {StyleSheet, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {Icon} from '@rneui/themed';

import {useModalDimensions} from '../../../hooks/useModalDimensions';
import {Address} from '../../../models/mfl';
import {AddressModalFormData, addressModalSchema} from '../../../schemas/checkoutSchemas';
import {useStores} from '../../../stores/use-stores';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import ValidationMessage from '../../themed/ValidationMessage';
import AddressEntry from '../../ui-controls/address/AddressEntry';
import CrossPlatformModal from '../../ui-controls/CrossPlatformModal';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

type Props = {
  children: ({handleOpen}: {handleOpen: () => void}) => React.ReactNode;
  addressType: 'delivery' | 'billing';
};

function AddressModal({children, addressType}: Props) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isManualEntryActive, setIsManualEntryActive] = React.useState(false);
  const {cartStore, userStore} = useStores();
  const {containerStyle: modalContainerStyle} = useModalDimensions({size: 'medium'});

  const isDelivery = addressType === 'delivery';
  const title = isDelivery ? 'Delivery address' : 'Billing address';
  const placeholder = isDelivery ? 'Delivery address' : 'Billing address';

  const form = useForm<AddressModalFormData>({
    resolver: zodResolver(addressModalSchema),
    defaultValues: {
      address: isDelivery ? cartStore.deliveryAddress : userStore.billingAddress,
    },
    mode: 'onTouched',
  });

  // Reset form and manual entry state when modal opens to sync with store values
  useEffect(() => {
    if (isVisible) {
      form.reset({
        address: isDelivery ? cartStore.deliveryAddress : userStore.billingAddress,
      });
      setIsManualEntryActive(false);
    }
  }, [isVisible, isDelivery, cartStore.deliveryAddress, userStore.billingAddress, form]);

  const onClose = () => {
    setIsVisible(false);
  };

  const handleOpen = () => setIsVisible(true);

  const onSubmit = async (values: AddressModalFormData) => {
    if (!values.address) return;

    if (isDelivery) {
      // Delivery flow
      if (cartStore.deliveryAddress !== values.address) {
        /**
         * have to clear timeslot because no longer valid
         * clear before setDelivery address otherwise it will attempt to validate order
         */
        cartStore.selectDay(null);
        cartStore.selectSlot(null);
        cartStore.setDeliveryAddress(values.address);
      }
      onClose();
    } else {
      // Billing flow
      if (userStore.billingAddress !== values.address) {
        try {
          await userStore.updateBillingAddress(values.address);
          onClose();
        } catch (error) {
          console.error('Failed to update billing address:', error);
          form.setError('address', {
            type: 'manual',
            message: 'Failed to update billing address. Please try again.',
          });
        }
      } else {
        onClose();
      }
    }
  };

  // Direct save+close for manual entry mode (bypasses form)
  const handleSubmitAndClose = async (address: Address) => {
    if (isDelivery) {
      cartStore.selectDay(null);
      cartStore.selectSlot(null);
      cartStore.setDeliveryAddress(address);
    } else {
      try {
        await userStore.updateBillingAddress(address);
      } catch (error) {
        console.error('Failed to update billing address:', error);
        return; // Don't close on error
      }
    }
    onClose();
  };

  return (
    <>
      {children({handleOpen})}
      <CrossPlatformModal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        avoidKeyboard
        keyboardAwareContent
        style={styles.modalContainer}>
        <View style={[styles.modalContent, modalContainerStyle]}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
            }}>
            <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
              {title}
            </Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>

          <View style={{padding: 20}}>
            <Controller
              control={form.control}
              name="address"
              render={({field: {onChange, value}, fieldState: {error, isTouched}}) => (
                <>
                  <AddressEntry
                    address={value}
                    onChange={(address: Address) => {
                      onChange(address);
                    }}
                    placeholder={placeholder}
                    inline
                    onSubmitAndClose={handleSubmitAndClose}
                    onModeChange={setIsManualEntryActive}
                  />
                  <ValidationMessage>{!!(isTouched && error) && error.message}</ValidationMessage>
                </>
              )}
            />
            {!isManualEntryActive && (
              <View style={{paddingTop: 40}}>
                <Button
                  loading={form.formState.isSubmitting}
                  disabled={form.formState.isSubmitting}
                  onPress={form.handleSubmit(onSubmit)}>
                  SAVE ADDRESS
                </Button>
              </View>
            )}
          </View>
        </View>
      </CrossPlatformModal>
    </>
  );
}

export default AddressModal;

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 4,
  },
});
